'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import Sidebar, { SidebarItem } from '@/components/layout/Sidebar';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase';
import { getDashboardRouteForRole } from '@/lib/role-routes';
import { startVisibilityAwarePolling } from '@/lib/polling';

const counselorNavItems: SidebarItem[] = [
  {
    label: 'Dashboard',
    href: '/counselor/dashboard',
    icon: (
      <svg data-component-id="src/app/counselor/layout.tsx:16:6" data-component-path="src/app/counselor/layout.tsx" data-component-line="16" data-component-end-line="23" data-component-file="layout.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path data-component-id="src/app/counselor/layout.tsx:17:8" data-component-path="src/app/counselor/layout.tsx" data-component-line="17" data-component-end-line="22" data-component-file="layout.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
        />
      </svg>
    ),
  },
  {
    label: 'Students',
    href: '/counselor/students',
    icon: (
      <svg data-component-id="src/app/counselor/layout.tsx:30:6" data-component-path="src/app/counselor/layout.tsx" data-component-line="30" data-component-end-line="37" data-component-file="layout.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path data-component-id="src/app/counselor/layout.tsx:31:8" data-component-path="src/app/counselor/layout.tsx" data-component-line="31" data-component-end-line="36" data-component-file="layout.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
        />
      </svg>
    ),
  },
  {
    label: 'Parents',
    href: '/counselor/parents',
    icon: (
      <svg data-component-id="src/app/counselor/layout.tsx:44:6" data-component-path="src/app/counselor/layout.tsx" data-component-line="44" data-component-end-line="51" data-component-file="layout.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path data-component-id="src/app/counselor/layout.tsx:45:8" data-component-path="src/app/counselor/layout.tsx" data-component-line="45" data-component-end-line="50" data-component-file="layout.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
    ),
  },
  {
    label: 'Tasks',
    href: '/counselor/tasks',
    icon: (
      <svg data-component-id="src/app/counselor/layout.tsx:58:6" data-component-path="src/app/counselor/layout.tsx" data-component-line="58" data-component-end-line="65" data-component-file="layout.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path data-component-id="src/app/counselor/layout.tsx:59:8" data-component-path="src/app/counselor/layout.tsx" data-component-line="59" data-component-end-line="64" data-component-file="layout.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
        />
      </svg>
    ),
  },
  {
    label: 'Meetings',
    href: '/counselor/meetings',
    icon: (
      <svg data-component-id="src/app/counselor/layout.tsx:72:6" data-component-path="src/app/counselor/layout.tsx" data-component-line="72" data-component-end-line="79" data-component-file="layout.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path data-component-id="src/app/counselor/layout.tsx:73:8" data-component-path="src/app/counselor/layout.tsx" data-component-line="73" data-component-end-line="78" data-component-file="layout.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    ),
  },
  {
    label: 'Messages',
    href: '/counselor/messages',
    icon: (
      <svg data-component-id="src/app/counselor/layout.tsx:86:6" data-component-path="src/app/counselor/layout.tsx" data-component-line="86" data-component-end-line="93" data-component-file="layout.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path data-component-id="src/app/counselor/layout.tsx:87:8" data-component-path="src/app/counselor/layout.tsx" data-component-line="87" data-component-end-line="92" data-component-file="layout.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
        />
      </svg>
    ),
  },
  {
    label: 'Availability',
    href: '/counselor/availability',
    icon: (
      <svg data-component-id="src/app/counselor/layout.tsx:100:6" data-component-path="src/app/counselor/layout.tsx" data-component-line="100" data-component-end-line="107" data-component-file="layout.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path data-component-id="src/app/counselor/layout.tsx:101:8" data-component-path="src/app/counselor/layout.tsx" data-component-line="101" data-component-end-line="106" data-component-file="layout.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
  },
  {
    label: 'Guidance Content',
    href: '/counselor/guidance',
    icon: (
      <svg data-component-id="src/app/counselor/layout.tsx:114:6" data-component-path="src/app/counselor/layout.tsx" data-component-line="114" data-component-end-line="121" data-component-file="layout.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path data-component-id="src/app/counselor/layout.tsx:115:8" data-component-path="src/app/counselor/layout.tsx" data-component-line="115" data-component-end-line="120" data-component-file="layout.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
        />
      </svg>
    ),
  },
  {
    label: 'Surveys',
    href: '/counselor/surveys',
    icon: (
      <svg data-component-id="src/app/counselor/layout.tsx:128:6" data-component-path="src/app/counselor/layout.tsx" data-component-line="128" data-component-end-line="135" data-component-file="layout.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path data-component-id="src/app/counselor/layout.tsx:129:8" data-component-path="src/app/counselor/layout.tsx" data-component-line="129" data-component-end-line="134" data-component-file="layout.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    ),
  },
  {
    label: 'Career Assessments',
    href: '/counselor/career-assessments',
    icon: (
      <svg data-component-id="src/app/counselor/layout.tsx:142:6" data-component-path="src/app/counselor/layout.tsx" data-component-line="142" data-component-end-line="150" data-component-file="layout.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22w-5%20h-5%22%7D" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path data-component-id="src/app/counselor/layout.tsx:143:8" data-component-path="src/app/counselor/layout.tsx" data-component-line="143" data-component-end-line="143" data-component-file="layout.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
        <path data-component-id="src/app/counselor/layout.tsx:144:8" data-component-path="src/app/counselor/layout.tsx" data-component-line="144" data-component-end-line="149" data-component-file="layout.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%7D"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z"
        />
      </svg>
    ),
  },
];

export default function CounselorLayout({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const [badgeCounts, setBadgeCounts] = useState<Record<string, number>>({});
  const [urgentBadges, setUrgentBadges] = useState<Record<string, boolean>>({});

  const isApproved = user?.approved === true;
  const shouldPauseBadgePolling =
    pathname.startsWith('/counselor/messages') ||
    pathname.startsWith('/counselor/tasks') ||
    pathname.startsWith('/counselor/dashboard');

  useEffect(() => {
    if (isLoading) return;

    if (!user) {
      router.push('/auth/login');
      return;
    }

    if (user.role !== 'counselor') {
      router.push(getDashboardRouteForRole(user.role));
    }
  }, [isLoading, user, router]);

  // Redirect unapproved counselors to dashboard only
  useEffect(() => {
    if (!isLoading && user && user.role === 'counselor' && !isApproved && pathname !== '/counselor/dashboard') {
      router.push('/counselor/dashboard');
    }
  }, [isLoading, user, isApproved, pathname, router]);

  // Compute badge counts
  const computeBadges = useCallback(async () => {
    if (!user) return;

    const counts: Record<string, number> = {};
    const urgent: Record<string, boolean> = {};

    const { data: pendingTasks } = await supabase
      .from('requests')
      .select('id,is_urgent')
      .eq('counselor_id', user.id)
      .eq('status', 'pending');

    if ((pendingTasks || []).length > 0) {
      counts['/counselor/tasks'] = pendingTasks!.length;
      urgent['/counselor/tasks'] = pendingTasks!.some((task) => task.is_urgent);
    }

    if (user.schoolId) {
      const [
        { data: studentRows, error: studentsError },
        { data: teacherRows, error: teachersError },
        { data: parentRows, error: parentRowsError },
        { count: pendingParentsCount, error: parentsError },
      ] = await Promise.all([
        supabase
          .from('profiles')
          .select('id,approved')
          .eq('school_id', user.schoolId)
          .eq('role', 'student'),
        supabase.from('profiles').select('id').eq('school_id', user.schoolId).eq('role', 'teacher'),
        supabase.from('profiles').select('id').eq('school_id', user.schoolId).eq('role', 'parent'),
        supabase
          .from('profiles')
          .select('id', { count: 'exact', head: true })
          .eq('school_id', user.schoolId)
          .eq('role', 'parent')
          .eq('student_confirmed', true)
          .eq('approved', false),
      ]);

      if (studentsError || teachersError || parentRowsError || parentsError) {
        setBadgeCounts(counts);
        setUrgentBadges(urgent);
        return;
      }

      // Unread badge covers all three contact tabs (students, teachers, parents)
      // on the Messages page, not just students.
      const contactIds = [
        ...(studentRows || []).map((row) => row.id),
        ...(teacherRows || []).map((row) => row.id),
        ...(parentRows || []).map((row) => row.id),
      ];

      if (contactIds.length > 0) {
        const keys = contactIds.map((contactId) => [contactId, user.id].sort().join('__'));
        const [{ data: messageRows }, { data: readRows }] = await Promise.all([
          supabase
            .from('messages')
            .select('conversation_key,sender_role,created_at')
            .in('conversation_key', keys)
            .neq('sender_role', 'counselor'),
          supabase
            .from('message_reads')
            .select('conversation_key,last_read_at')
            .eq('reader_id', user.id)
            .in('conversation_key', keys),
        ]);

        const readByConversation = new Map<string, string>();
        (readRows || []).forEach((row) => {
          readByConversation.set(row.conversation_key, row.last_read_at);
        });

        const totalUnread = (messageRows || []).reduce((sum, row) => {
          const lastReadAt = readByConversation.get(row.conversation_key);
          if (!lastReadAt || new Date(row.created_at).getTime() > new Date(lastReadAt).getTime()) {
            return sum + 1;
          }
          return sum;
        }, 0);

        if (totalUnread > 0) counts['/counselor/messages'] = totalUnread;
      }

      const pendingStudents = (studentRows || []).filter((student) => student.approved !== true).length;
      if (pendingStudents > 0) counts['/counselor/students'] = pendingStudents;
      if ((pendingParentsCount || 0) > 0) counts['/counselor/parents'] = pendingParentsCount!;
    }

    setBadgeCounts(counts);
    setUrgentBadges(urgent);
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;

    void computeBadges();
    if (shouldPauseBadgePolling) return;

    return startVisibilityAwarePolling(() => computeBadges(), 20000);
  }, [user?.id, computeBadges, shouldPauseBadgePolling]);

  if (isLoading) {
    return (
      <div data-component-id="src/app/counselor/layout.tsx:294:6" data-component-path="src/app/counselor/layout.tsx" data-component-line="294" data-component-end-line="299" data-component-file="layout.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20flex%20items-center%20justify-center%22%7D" className="min-h-screen bg-background flex items-center justify-center">
        <div data-component-id="src/app/counselor/layout.tsx:295:8" data-component-path="src/app/counselor/layout.tsx" data-component-line="295" data-component-end-line="298" data-component-file="layout.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%22%7D" className="text-center">
          <div data-component-id="src/app/counselor/layout.tsx:296:10" data-component-path="src/app/counselor/layout.tsx" data-component-line="296" data-component-end-line="296" data-component-file="layout.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-8%20h-8%20border-4%20border-primary%20border-t-transparent%20rounded-full%20animate-spin%20mx-auto%20mb-4%22%7D" className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p data-component-id="src/app/counselor/layout.tsx:297:10" data-component-path="src/app/counselor/layout.tsx" data-component-line="297" data-component-end-line="297" data-component-file="layout.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%22%2C%22textContent%22%3A%22Loading...%22%7D" className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || user.role !== 'counselor') {
    return null;
  }

  const visibleNavItems = isApproved
    ? counselorNavItems.map(item => ({
        ...item,
        badge: badgeCounts[item.href] || undefined,
        badgeVariant: urgentBadges[item.href] ? ('urgent' as const) : undefined,
      }))
    : counselorNavItems.filter(item => item.href === '/counselor/dashboard');

  return (
    <div data-component-id="src/app/counselor/layout.tsx:316:4" data-component-path="src/app/counselor/layout.tsx" data-component-line="316" data-component-end-line="330" data-component-file="layout.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      <Sidebar data-component-id="src/app/counselor/layout.tsx:317:6" data-component-path="src/app/counselor/layout.tsx" data-component-line="317" data-component-end-line="326" data-component-file="layout.tsx" data-component-name="Sidebar" data-component-content="%7B%22elementName%22%3A%22Sidebar%22%7D"
        items={visibleNavItems}
        userType="counselor"
        userName={user ? `${user.firstName} ${user.lastName}` : 'Counselor'}
        userEmail={user?.email || 'counselor@school.edu'}
        userAvatar={user?.profileImage}
        userSchool={user?.schoolName}
        userTitle={user?.title}
        userDepartment={user?.department}
      />
      <main data-component-id="src/app/counselor/layout.tsx:327:6" data-component-path="src/app/counselor/layout.tsx" data-component-line="327" data-component-end-line="329" data-component-file="layout.tsx" data-component-name="main" data-component-content="%7B%22elementName%22%3A%22main%22%2C%22className%22%3A%22lg%3Apl-64%20min-h-screen%22%7D" className="lg:pl-64 min-h-screen">
        <div data-component-id="src/app/counselor/layout.tsx:328:8" data-component-path="src/app/counselor/layout.tsx" data-component-line="328" data-component-end-line="328" data-component-file="layout.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-4%20sm%3Ap-6%20lg%3Ap-8%20pt-20%20lg%3Apt-8%22%7D" className="p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">{children}</div>
      </main>
    </div>
  );
}
